
import { getStreakRank, getRankDescription, getRankIcon } from "@/lib/streakRanks";
import { useEffect, useState } from "react";
import * as CookieManager from "@/lib/managers/CookieManager";
import { HeartCrack } from "lucide-react";
import { modeAwareStorage } from '@/lib/managers/ModeAwareStorageManager';
import { useViewMode } from "@/contexts/ViewModeContext";


interface StreakDisplayProps {
  currentStreak: number;
  previousStreak: number;
  savedGameStatus: string | null;
  onStreakCardOpen?: () => void;
  isPracticeMode?: boolean;
}

export const StreakDisplay = ({ 
  currentStreak, 
  previousStreak, 
  savedGameStatus,
  onStreakCardOpen,
  isPracticeMode = false
}: StreakDisplayProps) => {
  const { isLivestream } = useViewMode();
  // For practice mode, read streak from localStorage, otherwise use passed value
  const practiceStreak = isPracticeMode ? parseInt(localStorage.getItem('babblegram_practice_streak') || '0', 10) : 0;
  const practicePreviousStreak = isPracticeMode ? parseInt(localStorage.getItem('babblegram_practice_previous_streak') || '0', 10) : 0;

  const effectiveCurrentStreak = isPracticeMode ? practiceStreak : currentStreak;
  const effectivePreviousStreak = isPracticeMode ? practicePreviousStreak : previousStreak;
  
  const displayStreak = typeof effectiveCurrentStreak === 'number' && !isNaN(effectiveCurrentStreak) ? effectiveCurrentStreak : 0;
  const safeCurrentStreak = displayStreak || 0;
  const safePreviousStreak = (typeof effectivePreviousStreak === 'number' && !isNaN(effectivePreviousStreak)) ? effectivePreviousStreak : 0;
  
  console.log('StreakDisplay DEBUG:', {
    currentStreak,
    previousStreak,
    practiceStreak,
    practicePreviousStreak,
    displayStreak,
    safeCurrentStreak,
    safePreviousStreak,
    savedGameStatus,
    isPracticeMode
  });
  
  const streakRank = getStreakRank(safeCurrentStreak) || 'Baby Babbler';
  const previousRank = getStreakRank(safePreviousStreak) || 'Baby Babbler';
  
  // Use mode-aware storage for previous streak (daily mode only; practice uses localStorage above)
  const savedPreviousStreakValue = isPracticeMode ? null : modeAwareStorage.getCookie('previous_streak');
  const savedPreviousStreak = savedPreviousStreakValue ? parseInt(savedPreviousStreakValue) : null;
  const resolvedPreviousStreak = savedPreviousStreak !== null && savedPreviousStreak > 0 ? 
    savedPreviousStreak : safePreviousStreak;
  
  const hasRankedUp = streakRank !== previousRank && savedGameStatus === 'won';
  const hasLostStreak = (savedGameStatus === 'lost' && resolvedPreviousStreak > 0) || 
                        (safeCurrentStreak === 0 && resolvedPreviousStreak > 0);
  
  const [isFlipped, setIsFlipped] = useState(false);
  
  const streakNumber = hasLostStreak ? resolvedPreviousStreak : displayStreak;
  
  const streakImageNumber = Math.min(Math.max(Math.floor(streakNumber || 1), 1), 30);
  const streakImagePath = `/streak/${streakImageNumber}.PNG`;

  const streakDesc = getRankDescription(streakNumber || 1);
  const streakDescription = streakDesc || "Keep playing to increase your streak!";

  useEffect(() => {
    try {
      if (hasLostStreak) {
        const flipTimer = setTimeout(() => {
          setIsFlipped(true);
        }, 500);

        return () => {
          clearTimeout(flipTimer);
        };
      } else {
        setIsFlipped(false);
      }
    } catch (error) {
      console.error("Error in StreakDisplay effect:", error);
    }
  }, [hasLostStreak]);

  if ((savedGameStatus === 'lost' && resolvedPreviousStreak === 0 && safeCurrentStreak === 0) || !savedGameStatus) {
    return null;
  }

  const getColorScheme = (idx: number) => {
    const safeIdx = Math.max(1, Math.min(idx || 1, 30));
    const colorSchemes = [{
      primary: "#de0046",
      secondary: "#de0046",
      accent: "#de0046",
      gradient: "from-rose-500 via-rose-400 to-transparent"
    }, {
      primary: "#4361EE",
      secondary: "#3A0CA3",
      accent: "#4CC9F0",
      gradient: "from-blue-500 via-blue-400 to-transparent"
    }, {
      primary: "#FB8500",
      secondary: "#FFB703",
      accent: "#FD9E02",
      gradient: "from-amber-400 via-orange-400 to-transparent"
    }, {
      primary: "#8B5CF6",
      secondary: "#7209B7",
      accent: "#B5179E",
      gradient: "from-purple-500 via-violet-400 to-transparent"
    }, {
      primary: "#38B000",
      secondary: "#70E000",
      accent: "#4CBB17",
      gradient: "from-green-400 via-emerald-300 to-transparent"
    }];
    
    try {
      return colorSchemes[(safeIdx - 1) % colorSchemes.length];
    } catch (error) {
      console.error("Error getting color scheme:", error);
      return colorSchemes[0];
    }
  };

  const renderBackgroundIcons = (icon: React.ReactNode) => {
    try {
      const icons = [];
      const rows = 10;
      const cols = 15;
      
      const spacingX = 100 / (cols - 1);
      const spacingY = 100 / (rows - 1);
      
      for (let row = 0; row < rows; row++) {
        for (let col = 0; col < cols; col++) {
          const shouldRenderIcon = (row + col) % 2 === 0;
          if (shouldRenderIcon) {
            icons.push(
              <div 
                key={`${row}-${col}`} 
                className="absolute" 
                style={{
                  top: `${row * spacingY}%`,
                  left: `${col * spacingX}%`,
                  opacity: '0.10',
                  transform: 'translate(-50%, -50%)'
                }}
              >
                {icon}
              </div>
            );
          }
        }
      }
      return icons;
    } catch (error) {
      console.error("Error rendering background icons:", error);
      return null;
    }
  };

  const safeStreak = Math.max(1, Math.min(Math.floor(Number(streakNumber || 1)), 30));
  const colorScheme = getColorScheme(safeStreak);
  
  const IconComponent = getRankIcon(safeStreak);

  const safeColorScheme = {
    primary: colorScheme?.primary || "#38B000",
    secondary: colorScheme?.secondary || "#70E000",
    gradient: colorScheme?.gradient || "from-green-400 via-emerald-300 to-transparent"
  };

  const handleStreakCardClick = () => {
    if (onStreakCardOpen && !isFlipped) {
      console.log('Streak card clicked, opening rank card');
      onStreakCardOpen();
    }
  };

  return (
    <div 
      className={`streak-display mb-4 relative w-full ${isFlipped ? 'streak-flipped' : ''}`}
      style={{
        perspective: "1000px",
        transformStyle: "preserve-3d",
        height: "120px",
        transition: "transform 0.6s",
        transform: isFlipped ? "rotateY(180deg)" : "rotateY(0deg)"
      }}
    >
      <div 
        className="streak-card-front absolute w-full h-full overflow-hidden rounded-xl shadow-xl"
        style={{
          backfaceVisibility: "hidden",
          boxShadow: "0 20px 25px -5px rgba(0,0,0,0.3), 0 8px 10px -6px rgba(0,0,0,0.3)",
          fontFamily: "'Inter', system-ui, sans-serif",
          transformStyle: "preserve-3d",
          transform: "rotateY(0deg)",
          cursor: !isFlipped ? "pointer" : "default"
        }}
        onClick={handleStreakCardClick}
      >
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute inset-0 opacity-70">
            <div 
              className="absolute inset-0"
              style={{
                backgroundImage: `linear-gradient(to top, ${safeColorScheme.primary}, transparent)`
              }}
            />
          </div>
          <div className="absolute inset-0 bg-gray-900/40" />
          
          <div className="absolute inset-0 overflow-hidden">
            {IconComponent && renderBackgroundIcons(<IconComponent size={24} className="text-white" />)}
          </div>
          
          <div 
            className="streak-card-header absolute top-0 left-0 right-0 h-[68px]"
            style={{
              backgroundColor: safeColorScheme.primary,
              clipPath: 'polygon(0 0, 100% 0, 100% 70%, 0% 100%)'
            }}
          />
        </div>
        
        <div className="streak-card-content relative h-full flex flex-col px-4 pt-3">
          <div className="flex items-start justify-between z-10 pt-1 h-full">
            <div className="flex flex-col h-full w-[calc(60%+6px)] text-left">
              <div className="text-white font-bold text-2xl -mt-1 text-left"> 
                {streakRank}
              </div>
              
              <div className="text-white text-sm leading-tight m-0 p-0 text-left"> 
                {streakNumber} {isPracticeMode ? 'Win' : 'Day'}{streakNumber !== 1 ? 's' : ''}
              </div>
              
              <div className="mt-2 pt-1 pb-2">
                <div className="text-xs text-gray-200 z-10 flex items-center">
                  <div 
                    className="flex items-center leading-tight text-left"
                    style={{ transform: isLivestream ? 'translateY(-3px)' : undefined }}
                  >
                    {streakDescription}
                  </div>
                </div>
              </div>

            </div>
            
            <div className="h-full w-[calc(40%-6px)] relative">
              <img 
                src={streakImagePath} 
                alt={`Streak level ${streakNumber}`}
                className="h-[120px] w-auto object-contain absolute right-0 -top-3"
                onError={(e) => {
                  const target = e.target as HTMLImageElement;
                  target.src = '/streak/1.PNG';
                }}
              />
            </div>
          </div>
        </div>
        <div
          className="pointer-events-none absolute inset-0 z-20 rounded-xl border-[6px] border-solid"
          style={{ borderColor: safeColorScheme.primary }}
          aria-hidden="true"
        />
      </div>

      <div 
        className="streak-card-back absolute w-full h-full overflow-hidden rounded-xl shadow-xl"
        style={{
          backfaceVisibility: "hidden",
          transform: "rotateY(180deg)",
          boxShadow: "0 20px 25px -5px rgba(0,0,0,0.3), 0 8px 10px -6px rgba(0,0,0,0.3)",
          fontFamily: "'Inter', system-ui, sans-serif",
          transformStyle: "preserve-3d",
        }}
      >
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute inset-0 opacity-70">
            <div 
              className="absolute inset-0"
              style={{
                backgroundImage: `linear-gradient(to top, #de0046, transparent)`
              }}
            />
          </div>
          <div className="absolute inset-0 bg-gray-900/40" />
          
          <div className="absolute inset-0 overflow-hidden">
            {renderBackgroundIcons(<HeartCrack size={24} className="text-white" />)}
          </div>
          
          <div 
            className="absolute top-0 left-0 right-0 h-14"
            style={{
              backgroundColor: "#de0046",
              clipPath: 'polygon(0 0, 100% 0, 100% 70%, 0% 100%)'
            }}
          />
        </div>

        <div className="relative h-full flex items-center justify-center">
          <div className="text-white font-extrabold text-4xl z-10 tracking-wide">
            STREAK LOST!
          </div>
        </div>
        <div
          className="pointer-events-none absolute inset-0 z-20 rounded-xl border-[6px] border-solid border-[#de0046]"
          aria-hidden="true"
        />
      </div>
    </div>
  );
};
